import {
  IconArrowBackUp,
  IconBuildingHospital,
  IconLogout,
  IconMenu2,
  IconUserCircle,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, LanguageSwitch, Logo, Sheet } from '@/components/common';
import { navigate, useRoute } from '@/lib/router';
import type { User } from '@/types/api';
import { useAuth } from '../auth';
import { DoctorProvider } from '../doctors';
import { ProfilePage } from '../profile';
import { SettingsPage } from '../settings';
import { ClinicsPanel } from './ClinicsPanel';

/** The top bar and menu of a platform administrator, at the console or in a clinic. */
function PlatformShell({
  user,
  place,
  banner,
  children,
}: {
  user: User;
  /** Shown next to the brand: "Platform" or the clinic entered. */
  place: string;
  banner?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { logout, exitClinic, changeLanguage } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const go = (route: Parameters<typeof navigate>[0]) => {
    setMenuOpen(false);
    navigate(route);
  };

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar__start">
          <button
            type="button"
            className="topbar__brand"
            onClick={() => navigate({ name: user.support ? 'settings' : 'patients' })}
          >
            <Logo size={30} />
          </button>
        </div>
        <div className="topbar__center" />
        <div className="topbar__end">
          <span className="topbar__clinic">{place}</span>
          <button
            type="button"
            className="icon-btn"
            aria-label={t('shell.menu')}
            onClick={() => setMenuOpen(true)}
          >
            <IconMenu2 size={24} />
          </button>
        </div>
      </header>

      <main className="main">
        {banner && <div style={{ marginBottom: 16 }}>{banner}</div>}
        {children}
      </main>

      {menuOpen && (
        <Sheet title={user.name} onClose={() => setMenuOpen(false)}>
          <div className="stack">
            <p className="muted">{t('platform.role')}</p>
            <LanguageSwitch onChange={changeLanguage} />
            <nav className="stack" aria-label={t('shell.menu')}>
              {user.support ? (
                <button type="button" className="btn btn--block" onClick={exitClinic}>
                  <IconArrowBackUp size={20} /> {t('platform.exit')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn--block"
                    onClick={() => go({ name: 'patients' })}
                  >
                    <IconBuildingHospital size={20} /> {t('settings.clinics.title')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--block"
                    onClick={() => go({ name: 'profile' })}
                  >
                    <IconUserCircle size={20} /> {t('shell.profile')}
                  </button>
                </>
              )}
              <button type="button" className="btn btn--block btn--primary" onClick={logout}>
                <IconLogout size={20} /> {t('shell.logout')}
              </button>
            </nav>
          </div>
        </Sheet>
      )}
    </div>
  );
}

/**
 * A platform administrator's app. At the console: the clinics and their own
 * profile. In support mode: that clinic's settings and users, nothing with
 * patient data (the server refuses it too).
 */
export function PlatformApp({ user }: { user: User }) {
  const { t } = useTranslation();
  const { exitClinic } = useAuth();
  const route = useRoute();
  const clinic = user.support ? user.clinic : null;

  // Support mode has one screen; the console has the clinics and the profile.
  useEffect(() => {
    if (clinic && route.name !== 'settings') navigate({ name: 'settings' }, true);
    else if (!clinic && route.name !== 'patients' && route.name !== 'profile') {
      navigate({ name: 'patients' }, true);
    }
  }, [clinic, route.name]);

  if (clinic) {
    return (
      <PlatformShell
        user={user}
        place={clinic.name}
        banner={
          <Banner
            tone="info"
            action={
              <button type="button" className="btn btn--sm btn--dark" onClick={exitClinic}>
                <IconArrowBackUp size={18} /> {t('platform.exit')}
              </button>
            }
          >
            {t('platform.supportBanner', { name: clinic.name })}
          </Banner>
        }
      >
        {/* The users panel refreshes the clinic's doctors after a change. */}
        <DoctorProvider user={{ ...user, clinic }}>
          <SettingsPage />
        </DoctorProvider>
      </PlatformShell>
    );
  }

  return (
    <PlatformShell user={user} place={t('platform.title')}>
      {route.name === 'profile' ? <ProfilePage /> : <ClinicsPanel />}
    </PlatformShell>
  );
}
